// First-touch attribution capture (issue #204, epic #202).
//
// Captures inbound UTM params + ad click-ids from the landing URL and persists
// them (first-touch — never overwritten) so they survive the multi-step wizard
// and land on the `events` row at submit time. Runs at app entry (main.tsx).
//
// Storage is a first-party cookie, deliberately scoped to the shared parent
// domain `tristanoscatering.ca` (#486) — the app runs on `app.tristanoscatering.ca`,
// a different subdomain than the marketing site, and only a domain-scoped
// cookie survives that hop (sessionStorage, the prior mechanism, is
// per-origin and per-tab and never crossed it). No third-party transmission;
// capped at 90 days.
//
// The write respects cookie-consent state (#486): if the visitor has declined
// (via the app's own banner OR the marketing site's — both write to the same
// shared consent cookie), no attribution is captured, and an active Decline
// immediately purges any already-written attribution cookies. This matches
// marketing's behavior exactly.
//
// `fbclid` is also mirrored to the analytics FBCLID_STORAGE_KEY so the pixel's
// getFbc() match-key builder keeps working (issue #203).
//
// #1544 adds the first landing: the referrer's host and the first path opened,
// kept in two session cookies (no Max-Age, so they end with the browser
// session) and read by getFirstLanding() for the plan wizard's lead channel.
// They stay out of getAttribution(), whose keys are also spread into raw
// menu_inquiries inserts that have no columns for them.

import { FBCLID_STORAGE_KEY, sanitizeTrackedPath } from "./analytics";
import { analyticsAllowed } from "./consent";

// URL param → lead column. Cookies are named by param (tc_attr_ad_id), while
// getAttribution() is keyed by column, so jsonb RPCs and raw inserts get column
// names. The Meta ids (#1283) let a lead join to ad spend by ad id.
const ATTRIBUTION_FIELDS = [
  ["utm_source", "utm_source"],
  ["utm_medium", "utm_medium"],
  ["utm_campaign", "utm_campaign"],
  ["utm_content", "utm_content"],
  ["utm_term", "utm_term"],
  ["fbclid", "fbclid"],
  ["gclid", "gclid"],
  ["ad_id", "meta_ad_id"],
  ["adset_id", "meta_adset_id"],
  ["campaign_id", "meta_campaign_id"],
] as const;
const ATTRIBUTION_KEYS = ATTRIBUTION_FIELDS.map(([param]) => param);

// Meta ids are numeric. This drops a literal "{{ad.id}}" (an unexpanded macro
// in a preview or share link) so it can't take the first-touch slot.
const META_ID_RE = /^\d{1,32}$/;
const META_ID_PARAMS: ReadonlySet<string> = new Set(["ad_id", "adset_id", "campaign_id"]);

type AttributionColumn = (typeof ATTRIBUTION_FIELDS)[number][1];
export type Attribution = Record<AttributionColumn, string | null>;

const STORAGE_PREFIX = "tc_attr_";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90-day attribution window
const SHARED_PARENT_DOMAIN = "tristanoscatering.ca";
const MAX_VALUE_LENGTH = 512; // #486 — see writeFirstTouch for rationale
const REFERRER_HOST_KEY = `${STORAGE_PREFIX}referrer_host`;
const LANDING_PATH_KEY = `${STORAGE_PREFIX}landing_path`;
const MAX_LANDING_PATH_LENGTH = 150;

// Derived from ATTRIBUTION_KEYS + STORAGE_PREFIX rather than hand-listed, so
// a future key can't silently drift out of the clear list. FBCLID_STORAGE_KEY
// ("tc_fbclid") is included because it's a 90-day shared-domain cookie too (a live
// Meta CAPI match key) — Decline must remove it exactly like the tc_attr_* keys.
const ATTRIBUTION_COOKIE_KEYS = [
  ...ATTRIBUTION_KEYS.map((k) => STORAGE_PREFIX + k),
  FBCLID_STORAGE_KEY,
  REFERRER_HOST_KEY,
  LANDING_PATH_KEY,
] as const;

/** Pure — testable without touching window.location. */
export function cookieDomainAttr(hostname: string): string {
  if (hostname === SHARED_PARENT_DOMAIN || hostname.endsWith(`.${SHARED_PARENT_DOMAIN}`)) {
    return `; Domain=.${SHARED_PARENT_DOMAIN}`;
  }
  return ""; // localhost, *.vercel.app previews, etc. — host-only cookie
}

/**
 * Pure — the exact string handed to `document.cookie =`. Split out so a typo
 * in attribute order/spelling (a missing leading dot, a swallowed `Path=/`,
 * `Secure` emitted on http) is caught by a direct unit test instead of only
 * surfacing on the real cross-domain check post-merge (#486).
 */
export function buildAttributionCookie(
  key: string,
  value: string,
  hostname: string,
  protocol: string,
  maxAgeSeconds: number | null = COOKIE_MAX_AGE_SECONDS,
): string {
  const secure = protocol === "https:" ? "; Secure" : "";
  // null = a session cookie (#1544): no Max-Age, so it ends with the browser session.
  const maxAge = maxAgeSeconds === null ? "" : `; Max-Age=${maxAgeSeconds}`;
  return `${key}=${encodeURIComponent(value)}${maxAge}; Path=/${cookieDomainAttr(hostname)}; SameSite=Lax${secure}`;
}

function read(key: string): string | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeFirstTouch(
  key: string,
  value: string,
  maxAgeSeconds: number | null = COOKIE_MAX_AGE_SECONDS,
): void {
  try {
    if (!analyticsAllowed()) return; // correct for the returning-decliner case; see clearAttribution() for a first-time decline
    // #486 — measured post-encode: percent-encoding can expand a value up to
    // ~6x, and the cap exists to bound the actual bytes on the wire (this
    // cookie rides every request header for 90 days), not the pre-encode
    // character count.
    if (encodeURIComponent(value).length > MAX_VALUE_LENGTH) return;
    if (read(key)) return; // first-touch wins
    document.cookie = buildAttributionCookie(
      key,
      value,
      window.location.hostname,
      window.location.protocol,
      maxAgeSeconds,
    );
  } catch {
    // cookies can be blocked in privacy modes — capture is best-effort.
  }
}

/**
 * Read attribution params from the current URL and persist any present ones
 * (first-touch — an already-stored value is never clobbered). Safe to call on
 * every entry; reads `location.search` only (never the hash). Never throws.
 */
export function captureAttribution(): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  // #892 — a paid click with only a click-id leaves utm_source empty, and a
  // later internal CTA (utm_source=website, #889) would fill it. A stored
  // click-id with none in this URL means a follow-on visit, not a new
  // acquisition, so it doesn't get to set any utm_* key (#916: utm_content /
  // utm_term too, and any utm_* key added later). That includes any
  // UTM-only link into the app (a newsletter too); marketing's own capture
  // isn't guarded. Read before the loop so this only sees earlier visits.
  // A Meta ad id (#1283) counts as a paid click too.
  // #1576 — the /ig bio hub's own tag (utm_medium=bio) is exempt: Instagram
  // adds fbclid to organic bio clicks, so a bare stored fbclid there is not a
  // paid click. A stored gclid or Meta ad id is, and still suppresses.
  const storedGclid = read(STORAGE_PREFIX + "gclid");
  const storedMetaAdId = read(STORAGE_PREFIX + "ad_id");
  const storedAdClickId =
    storedGclid ||
    read(STORAGE_PREFIX + "fbclid") ||
    read(FBCLID_STORAGE_KEY) ||
    storedMetaAdId;
  const urlAdId = params.get("ad_id");
  const urlHasAdClickId = Boolean(
    params.get("gclid") || params.get("fbclid") || (urlAdId && META_ID_RE.test(urlAdId)),
  );
  const urlIsOwnBioTag = params.get("utm_medium")?.trim().toLowerCase() === "bio";
  const suppressUtm =
    Boolean(storedAdClickId) && !urlHasAdClickId && !(urlIsOwnBioTag && !storedMetaAdId && !storedGclid);
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (!value) continue;
    if (META_ID_PARAMS.has(key) && !META_ID_RE.test(value)) continue;
    if (suppressUtm && key.startsWith("utm_")) continue;
    writeFirstTouch(STORAGE_PREFIX + key, value);
    // Keep the pixel's fbc builder (issue #203) in sync with first-touch fbclid.
    if (key === "fbclid") writeFirstTouch(FBCLID_STORAGE_KEY, value);
  }
  captureFirstLanding();
}

/** Pure — the referrer's hostname, lowercased; null when empty or unparseable. Never throws. */
export function referrerHost(referrer: string): string | null {
  if (!referrer) return null;
  try {
    return new URL(referrer).hostname.toLowerCase() || null;
  } catch {
    return null;
  }
}

/** Pure — the path with no query or hash, `/quote/<id>` as `/quote/:id`, capped at 150 chars. */
export function normalizeLandingPath(pathname: string): string {
  return sanitizeTrackedPath(pathname).slice(0, MAX_LANDING_PATH_LENGTH) || "/";
}

// #1544 — first landing only. The landing path is written last, so it marks
// the capture as done; a later page load in the same session changes nothing.
function captureFirstLanding(): void {
  try {
    if (read(LANDING_PATH_KEY)) return;
    const host = referrerHost(document.referrer);
    if (host) writeFirstTouch(REFERRER_HOST_KEY, host, null);
    writeFirstTouch(LANDING_PATH_KEY, normalizeLandingPath(window.location.pathname), null);
  } catch {
    // best-effort, like every other cookie access in this file
  }
}

/** The first landing of this browser session (#1544). */
export function getFirstLanding(): { referrer_host: string | null; landing_path: string | null } {
  return { referrer_host: read(REFERRER_HOST_KEY), landing_path: read(LANDING_PATH_KEY) };
}

/** Return the stored first-touch attribution, keyed by `events` column name. */
export function getAttribution(): Attribution {
  return ATTRIBUTION_FIELDS.reduce((acc, [param, column]) => {
    acc[column] = read(STORAGE_PREFIX + param);
    return acc;
  }, {} as Attribution);
}

/**
 * Same as getAttribution(), with every null key dropped (#486). Use this —
 * never getAttribution() directly — when spreading into a raw PostgREST
 * `.insert()` (as opposed to a `jsonb`-arg RPC). A raw insert names every
 * spread key as a column and PostgREST rejects the WHOLE request
 * (`PGRST204`) if any named column isn't in its schema cache yet — which
 * happens for a few minutes on every deploy where a migration adds the
 * column in the same push (the frontend deploy and the migration sync have
 * no ordering dependency). getAttribution() always names all 10 keys, so an
 * un-filtered spread would fail every submission, not just attributed ones,
 * during that window. An RPC's jsonb payload has no such problem — unknown
 * keys are silently ignored server-side — so RPC-based callers should keep
 * using getAttribution() as-is.
 */
export function getAttributionForInsert(): Partial<Attribution> {
  const attribution = getAttribution();
  return Object.fromEntries(
    Object.entries(attribution).filter(([, value]) => value !== null),
  ) as Partial<Attribution>;
}

/**
 * Expire every attribution cookie immediately. Called on Decline (see
 * disableAnalytics()) so the button actually does something for the common
 * case — a first-time visitor whose cookies were already written before
 * Decline existed on screen (capture runs in main.tsx's module-level code,
 * the banner renders after). Reuses cookieDomainAttr() for the SAME Domain
 * attribute the cookie was set with — a mismatch would make the browser
 * treat this as a distinct, unrelated cookie rather than deleting the real
 * one. Matches by name + Domain + Path only (not by which app wrote it), so
 * this also purges a tc_attr_ or tc_fbclid value written by a direct-to-marketing
 * ad click (#486).
 */
export function clearAttribution(): void {
  try {
    const domain = cookieDomainAttr(window.location.hostname);
    for (const key of ATTRIBUTION_COOKIE_KEYS) {
      document.cookie = `${key}=; Max-Age=0; Path=/${domain}`;
    }
  } catch {
    // best-effort, matches every other storage access in this file
  }
}
